import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useUlozMarzeKategorii,
  type MarzeKategoriiPayload,
  type PoleMarze,
} from "@/api/hooks/useCennik";
import { formatPocet, sklonuj } from "@/lib/format";
import type { KartaMarzi } from "@/components/cennik/marzeKarty";
import {
  KATEGORII,
  POLOZIEK,
  nazovPola,
  overHodnotu,
  popisUlozenia,
} from "@/components/cennik/marzeFormat";

type Props = {
  /** Karta, ktorej sa nastavuje; `null` = okno zatvorené. */
  karta: KartaMarzi | null;
  onClose: () => void;
};

/**
 * „Nastaviť celej sekcii" (KOLO 166) — jedna sada čísel pre všetky
 * kategórie karty. Posiela výslovné páry (druh, kategória) karty, takže
 * nič nepretečie do inej jednotky; prázdne pole sa nemení.
 */
export function MarzeSekciaDialog({ karta, onClose }: Props): JSX.Element {
  return (
    <Dialog open={karta !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        {/* Telo s kľúčom karty — každé otvorenie začína prázdne */}
        {karta ? <Telo key={karta.kluc} karta={karta} onClose={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

function Telo({ karta, onClose }: { karta: KartaMarzi; onClose: () => void }): JSX.Element {
  const [hodnoty, setHodnoty] = useState<Partial<Record<PoleMarze, string>>>({});
  const [ajVlastne, setAjVlastne] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);
  const ulozit = useUlozMarzeKategorii();

  const kategorii = karta.polozky.length;
  const poloziek = karta.polozky.reduce((s, p) => s + p.pocet, 0);
  const vlastnych = karta.polozky.reduce((s, p) => s + p.pocet_vlastnych, 0);
  const zmeni = ajVlastne ? poloziek : poloziek - vlastnych;

  const zadane = karta.polia.filter((p) => (hodnoty[p] ?? "").trim() !== "");

  async function potvrd(): Promise<void> {
    setChyba(null);
    const payload: MarzeKategoriiPayload = {
      polozky: karta.polozky.map((p) => ({ druh: p.druh, kategoria: p.kategoria })),
      aj_vlastne: ajVlastne,
    };
    for (const pole of zadane) {
      const overena = overHodnotu(pole, hodnoty[pole] ?? "");
      if (overena === null) {
        setChyba(`${nazovPola(pole, karta)}: neplatné číslo.`);
        return;
      }
      payload[pole] = overena;
    }
    try {
      const r = await ulozit.mutateAsync(payload);
      toast({
        title: "Marže uložené",
        description: popisUlozenia(karta.nazov, r.data),
      });
      onClose();
    } catch (err) {
      setChyba(extractErrorMessage(err));
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Nastaviť celej sekcii — {karta.nazov}</DialogTitle>
        <DialogDescription>
          Vyplň len to, čo chceš zmeniť. Prázdne pole ostane, ako je.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        {karta.polia.map((pole) => (
          <div key={pole} className="space-y-1">
            <Label htmlFor={`sekcia-${pole}`}>{nazovPola(pole, karta)}</Label>
            <Input
              id={`sekcia-${pole}`}
              inputMode="decimal"
              placeholder="nemeniť"
              value={hodnoty[pole] ?? ""}
              onChange={(e) =>
                setHodnoty((h) => ({ ...h, [pole]: e.target.value }))
              }
            />
          </div>
        ))}

        {vlastnych > 0 ? (
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={ajVlastne}
              onChange={(e) => setAjVlastne(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            Aj položky s vlastnými maržami ({formatPocet(vlastnych)})
          </label>
        ) : null}

        <p className="text-sm text-muted-foreground" data-testid="sekcia-suhrn">
          Zmení {formatPocet(kategorii)} {sklonuj(kategorii, KATEGORII)} a najviac{" "}
          {formatPocet(zmeni)} {sklonuj(zmeni, POLOZIEK)}
          {vlastnych > 0 && !ajVlastne
            ? `; ${formatPocet(vlastnych)} s vlastnými maržami bez zmeny.`
            : "."}
        </p>
        {chyba ? <p className="text-sm text-destructive">{chyba}</p> : null}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Zrušiť
        </Button>
        <Button
          onClick={() => void potvrd()}
          disabled={zadane.length === 0 || ulozit.isPending}
        >
          Uložiť
        </Button>
      </DialogFooter>
    </>
  );
}
